import { Element, Node, Text } from "slate";
import { RenderElementProps, RenderLeafProps } from "slate-react";
import Button from "../../components/lib/Button/Button";

export interface NodeProps extends Element {
  url?: string;
}

const blockTypes = [
  "blockquote",
  "heading-1",
  "heading-2",
  "heading-3",
  "heading-4",
  "heading-5",
  "heading-6",
  "button",
  "ordered-list",
  "unordered-list",
  "list-item",
  "paragraph",
];

const renderedElement = (
  element: Element | NodeProps,
  children: any,
  attributes: any
) => {
  switch (element.type) {
    case "blockquote":
      return (
        <blockquote
          className="py-4 pl-4 border-l-4 border-gray-400"
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case "heading-1":
      return (
        <h1 {...attributes} className={`h1 ${element.className}`}>
          {children}
        </h1>
      );
    case "heading-2":
      return (
        <h2 {...attributes} className={`h2 ${element.className}`}>
          {children}
        </h2>
      );
    case "heading-3":
      return (
        <h3 {...attributes} className={`h3 ${element.className}`}>
          {children}
        </h3>
      );
    case "heading-4":
      return (
        <h4 {...attributes} className={`h4 ${element.className}`}>
          {children}
        </h4>
      );
    case "heading-5":
      return (
        <h5 {...attributes} className={`h5 ${element.className}`}>
          {children}
        </h5>
      );
    case "heading-6":
      return (
        <h6 {...attributes} className={`h6 ${element.className}`}>
          {children}
        </h6>
      );
    case "button":
      if (Object.keys(attributes).length > 0) {
        return (
          <div
            {...attributes}
            className={`btn ${
              element.buttonType === "primary" ? "btn-primary" : "btn-outline"
            } ${element.className}`}
          >
            {children}
          </div>
        );
      } else {
        return (
          <Button
            variant="primary"
            className={`${element.className}`}
            type="submit"
          >
            {children}
          </Button>
        );
      }
    case "bulleted-list":
      return (
        <ul {...attributes} className={`${element.className}`}>
          {children}
        </ul>
      );
    case "list-item":
      return (
        <li {...attributes} className={`${element.className}`}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol {...attributes} className={`${element.className}`}>
          {children}
        </ol>
      );
    default:
      return (
        <p {...attributes} className={`${element.className}`}>
          {children}
        </p>
      );
  }
};

const renderedLeaf = (leaf: Text, children: any, attributes: any) => {
  const classNames = [];
  if (leaf.color) classNames.push(leaf.color);
  if (leaf.fontSize) classNames.push(leaf.fontSize);
  if (leaf.fontWeight) classNames.push(leaf.fontWeight);
  if (leaf.bold) classNames.push("font-bold");
  if (leaf.italic) classNames.push("italic");
  if (leaf.underline) classNames.push("underline");
  if (leaf.code) {
    return (
      <code {...attributes} className={classNames.join(" ")} key={leaf}>
        {children}
      </code>
    );
  }
  return (
    <span {...attributes} className={classNames.join(" ")} key={leaf}>
      {children}
    </span>
  );
};

export const renderElement = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  return renderedElement(element, children, attributes);
};
export const renderLeaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  return renderedLeaf(leaf, children, attributes);
};

export const renderJSX = (element: Node) => {
  if (Text.isText(element)) {
    return element.text;
  }
  const children: any = element.children.map((n, index) => {
    if (blockTypes.includes(`${n.type}`)) {
      return renderJSX(n);
    } else {
      return renderJSXLeaf(n);
    }
  });
  return renderedElement(element, children, {});
};

const renderJSXLeaf = (leaf: Node) => {
  if (Text.isText(leaf)) {
    if (Object.keys(leaf).length === 1 && leaf.text) {
      return leaf.text;
    }
    return renderedLeaf(leaf, leaf.text, {});
  } else {
    return "Ungültig";
  }
};
